import { apiClient } from "@/lib/api-client"
import type { OffboardingDto } from "@/modules/offboarding/dtos"

export interface AdminInitiateOffboardingPayload {
  employeeId: string
  offboardingReason: string
  lastWorkingDate: string
  workflowTemplateIds?: string[]
  comments?: string
  requiredDocuments?: Array<{
    documentName: string
    employeeEditable: boolean
  }>
}

export interface PatchOffboardingPayload {
  offboardingReason?: string
  lastWorkingDate?: string
  workflowTemplateIds?: string[]
  resignationStatus?: string
  gracePeriodDays?: number
  comments?: string
  documents?: Array<{
    id: string
    documentName: string
    employeeEditable: boolean
  }>
  requiredDocuments?: Array<{
    documentName: string
    employeeEditable: boolean
  }>
}

class OffboardingService {
  async adminInitiate(
    organizationId: string,
    payload: AdminInitiateOffboardingPayload,
  ): Promise<void> {
    await apiClient.post("/EmployeeOffboarding/admin-initiate", payload, {
      headers: { "X-Organization-Id": organizationId },
    })
  }

  async patch(
    organizationId: string,
    id: string,
    payload: PatchOffboardingPayload,
  ): Promise<void> {
    await apiClient.patch(`/EmployeeOffboarding/${id}`, payload, {
      headers: { "X-Organization-Id": organizationId },
    })
  }

  // Case 1: fill existing placeholder — pass existingDocumentId
  // Case 2: add new document — pass documentName + employeeEditable
  async uploadDocument(
    organizationId: string,
    offboardingId: string,
    file: File,
    options: { existingDocumentId: string } | { documentName: string; employeeEditable: boolean },
  ): Promise<void> {
    const formData = new FormData()
    formData.append("File", file)
    if ("existingDocumentId" in options) {
      formData.append("ExistingDocumentId", options.existingDocumentId)
    } else {
      formData.append("DocumentName", options.documentName)
      formData.append("EmployeeEditable", String(options.employeeEditable))
    }
    await apiClient.post(`/EmployeeOffboarding/${offboardingId}/documents`, formData, {
      headers: {
        "X-Organization-Id": organizationId,
        "Content-Type": "multipart/form-data",
      },
    })
  }

  async downloadDocument(
    organizationId: string,
    offboardingId: string,
    documentId: string,
  ): Promise<{ sasUrl: string; fileName: string }> {
    const { data } = await apiClient.get<{ value: { sasUrl: string; fileName: string } }>(
      `/EmployeeOffboarding/${offboardingId}/documents/${documentId}/download`,
      { headers: { "X-Organization-Id": organizationId } },
    )
    return data.value
  }

  async getOffboardingById(organizationId: string, id: string): Promise<OffboardingDto> {
    const { data } = await apiClient.get<OffboardingDto>(
      `/EmployeeOffboarding/${id}`,
      { headers: { "X-Organization-Id": organizationId } },
    )
    return data
  }

  async getOffboardings(organizationId: string, searchTerm?: string, resignationStatus?: string): Promise<OffboardingDto[]> {
    const { data } = await apiClient.get<OffboardingDto[]>(
      "/EmployeeOffboarding",
      {
        headers: { "X-Organization-Id": organizationId },
        params: {
          ...(searchTerm && { searchTerm }),
          ...(resignationStatus && { resignationStatus }),
        },
      },
    )
    return data
  }
}

export const offboardingService = new OffboardingService()
